import React, { useEffect, useMemo, useState } from 'react';
import {
  IMPORT_PREVIEW_SOURCES,
  describeAppPayloadSourceKind,
  fetchDdbAppPayload,
  fetchImportAppPayload,
  fetchImportPdfUploadAppPayload,
  getImportPreviewTransport,
  summarizeImportReviewPayload,
  validateImportReviewPayload,
} from './appPayloadReview';
import { CHARACTER_PDF_UPLOAD_SOURCE } from './productionUploadClient';
import { applyReviewedImportPayload } from './applyImportPayload';

const DEFAULT_IMPORT_API_BASE = process.env.REACT_APP_IMPORT_API_BASE
  || (process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:3001/importer' : '');
const GENERIC_APPLY_FAILURE_MESSAGE = 'Import apply failed. Check the character storage state and try again.';

// Theme tokens (2026-07-14): every color routes through a CSS custom property
// with the original literal as the fallback. Hosts that pass `themeVars`
// (the dashboard) restyle the whole modal; standalone mounts (legacy select
// screen, CampaignLanding) render exactly as before via the fallbacks.
const CI = {
  overlayBg: 'var(--ci-overlay-bg, rgba(0, 0, 0, 0.72))',
  surface: 'var(--ci-surface, #141922)',
  text: 'var(--ci-text, #f6f1df)',
  textSoft: 'var(--ci-text-soft, #c8beaa)',
  textMuted: 'var(--ci-text-muted, #b9b0a0)',
  textLabel: 'var(--ci-text-label, #d8cfb7)',
  accent: 'var(--ci-accent, #d6b14c)',
  accentBorder: 'var(--ci-accent-border, rgba(214, 177, 76, 0.62))',
  accentBorderSoft: 'var(--ci-accent-border-soft, rgba(214, 177, 76, 0.24))',
  border: 'var(--ci-border, rgba(255, 255, 255, 0.2))',
  panelBorder: 'var(--ci-panel-border, rgba(255, 255, 255, 0.14))',
  panelBg: 'var(--ci-panel-bg, rgba(255, 255, 255, 0.045))',
  inputBg: 'var(--ci-input-bg, #0f131a)',
  btnBg: 'var(--ci-btn-bg, #2d3c4e)',
  btnText: 'var(--ci-btn-text, #fff7d6)',
  btnSecondaryBg: 'var(--ci-btn-secondary-bg, #242a34)',
  btnDisabledBg: 'var(--ci-btn-disabled-bg, #333740)',
  dangerBg: 'var(--ci-danger-bg, #5a2730)',
  dangerBorder: 'var(--ci-danger-border, rgba(220, 91, 91, 0.74))',
  warnBg: 'var(--ci-warn-bg, rgba(214, 177, 76, 0.09))',
  warnBorder: 'var(--ci-warn-border, rgba(214, 177, 76, 0.56))',
  radius: 'var(--ci-radius, 8px)',
  radiusSm: 'var(--ci-radius-sm, 6px)',
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  background: CI.overlayBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const modalStyle = {
  width: 'min(960px, 100%)',
  maxHeight: '92vh',
  overflow: 'auto',
  background: CI.surface,
  color: CI.text,
  border: `1px solid ${CI.accentBorder}`,
  borderRadius: CI.radius,
  boxShadow: '0 24px 80px rgba(0, 0, 0, 0.55)',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  padding: '18px 20px 14px',
  borderBottom: `1px solid ${CI.accentBorderSoft}`,
};

const bodyStyle = {
  padding: 20,
  display: 'grid',
  gap: 16,
};

const controlsStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
  alignItems: 'end',
};

const panelStyle = {
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: CI.panelBorder,
  borderRadius: CI.radius,
  padding: 14,
  background: CI.panelBg,
};

function safeApplyErrorMessage(error) {
  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  if (!message) return GENERIC_APPLY_FAILURE_MESSAGE;
  if (/Could not find public function|No public function|createStandaloneCharacter/i.test(message)) {
    return 'Import backend is out of date. Restart Convex dev and reload the app.';
  }
  if (/raw character creation is not allowed while a campaign is active/i.test(message)) {
    return 'Leave campaign mode before creating an imported standalone character.';
  }
  if (/not authenticated/i.test(message)) return 'Sign in before applying this import.';
  if (/character already exists/i.test(message)) return 'Imported character already exists. Reload and try again.';
  const wordCount = (message.match(/[A-Za-z0-9]+/g) || []).length;
  if (
    message.length > 160
    || /[\r\n<>`{}]/.test(message)
    || /https?:\/\//i.test(message)
    || /\b(?:copyright|copied|description|full text|private|prose|raw|source text|provider|stdout|stderr|ocr|pdf object|FeaturesTraits)\b/i.test(message)
    || (/[.!?]/.test(message) && wordCount > 3)
    || wordCount > 16
  ) {
    return GENERIC_APPLY_FAILURE_MESSAGE;
  }
  return message;
}

const labelStyle = {
  display: 'grid',
  gap: 6,
  fontSize: 13,
  color: CI.textLabel,
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: CI.radiusSm,
  border: `1px solid ${CI.border}`,
  background: CI.inputBg,
  color: CI.text,
  padding: '10px 11px',
};

const buttonStyle = {
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: CI.accentBorder,
  borderRadius: CI.radiusSm,
  background: CI.btnBg,
  color: CI.btnText,
  padding: '10px 12px',
  cursor: 'pointer',
  minHeight: 40,
};

const secondaryButtonStyle = {
  ...buttonStyle,
  background: CI.btnSecondaryBg,
  borderColor: CI.border,
};

const disabledButtonStyle = {
  ...buttonStyle,
  cursor: 'not-allowed',
  opacity: 0.58,
  background: CI.btnDisabledBg,
  borderColor: CI.panelBorder,
};

const dangerButtonStyle = {
  ...buttonStyle,
  background: CI.dangerBg,
  borderColor: CI.dangerBorder,
};

const warningPanelStyle = {
  ...panelStyle,
  borderColor: CI.warnBorder,
  background: CI.warnBg,
};

function joinList(values, fallback = 'None') {
  if (!Array.isArray(values) || values.length === 0) return fallback;
  return values.join(', ');
}

function redactPayloadForDisplay(value) {
  if (!value || typeof value !== 'object') return value;
  const next = JSON.parse(JSON.stringify(value));
  if (next?.createArgs?.password !== undefined) delete next.createArgs.password;
  return next;
}

function CountGrid({ summary }) {
  const items = [
    ['SRD rows', summary.counts.srd],
    ['Name-only rows', summary.counts.placeholders],
    ['Abilities', summary.counts.abilities],
    ['Feats', summary.counts.feats],
    ['Spells', summary.counts.spells],
    ['Inventory', summary.counts.inventory],
    ['Diagnostics', summary.counts.diagnostics],
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
      {items.map(([label, value]) => (
        <div key={label} style={{ ...panelStyle, padding: 10 }}>
          <div style={{ fontSize: 12, color: CI.textMuted }}>{label}</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
        </div>
      ))}
    </div>
  );
}

function getFlowWarnings(payload, summary) {
  if (!payload || !summary) return [];
  const warnings = [];
  const patch = payload.patch || {};
  const isDiceCloud = summary.sourceKind === 'dicecloud-core-facts';

  if (summary.equipmentStatus === 'deferred') {
    warnings.push('Equipment was detected but is deferred. Enable Include equipment payload and load the review again to include inventory, currency, and carrying data.');
  } else if (summary.equipmentStatus === 'included') {
    warnings.push('Equipment is included. Applying this import will write the reviewed inventory, currency, and carrying fields present in the payload.');
  }

  if (Array.isArray(patch.spells)) {
    const count = patch.spells.length;
    warnings.push(
      payload.mode === 'update'
        ? `Spell list replacement: applying this update will replace the character's spell list with ${count} reviewed spell${count === 1 ? '' : 's'}.`
        : `Spell list included: the new character will start with ${count} reviewed spell${count === 1 ? '' : 's'}.`
    );
  }
  if (payload.mode === 'update' && summary.warnings?.featureListReplacement) {
    const count = (Array.isArray(patch.abilities) ? patch.abilities.length : 0)
      + (Array.isArray(patch.feats) ? patch.feats.length : 0);
    warnings.push(`Feature list replacement: applying this update will replace reviewed abilities and feats with ${count} reviewed row${count === 1 ? '' : 's'}.`);
  }
  if (isDiceCloud && (summary.warnings?.skillsOmitted || summary.warnings?.savesOmitted)) {
    warnings.push('Skills and saving throw proficiencies are omitted for this DiceCloud review because this format has not exposed those facts confidently yet.');
  }
  if (isDiceCloud && summary.warnings?.preparedStateUnknown) {
    warnings.push('Spell prepared/unprepared state is unknown for this DiceCloud review; reviewed spells are name/SRD rows only.');
  }
  if (isDiceCloud && summary.warnings?.attackRowsReviewOnly) {
    warnings.push('Weapon attack rows were detected for review, but they are not materialized as tracker actions yet.');
  }
  if (isDiceCloud && summary.warnings?.sourceSrdLevelConflict) {
    warnings.push('At least one spell has a source-level and SRD-level conflict; the review uses the local SRD definition and keeps the conflict as a diagnostic.');
  }

  return warnings;
}

function FlowWarnings({ payload, summary }) {
  const warnings = getFlowWarnings(payload, summary);
  if (!warnings.length) return null;
  return (
    <section style={warningPanelStyle} aria-label="Import flow warnings">
      <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Review Warnings</h3>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {warnings.map((warning) => (
          <li key={warning} style={{ marginBottom: 6 }}>{warning}</li>
        ))}
      </ul>
    </section>
  );
}

const SOURCE_KIND_BADGES = {
  srd: { label: 'SRD', color: '#63be7b' },
  'custom-placeholder': { label: 'name-only', color: CI.accent },
  'character-fact': { label: 'fact', color: '#8fb5d6' },
};

function ProvenanceBadge({ sourceKind }) {
  const badge = SOURCE_KIND_BADGES[sourceKind] || { label: sourceKind, color: CI.textMuted };
  return (
    <span style={{
      border: `1px solid ${badge.color}`,
      color: badge.color,
      borderRadius: 9,
      padding: '0 7px',
      fontSize: 11,
      marginRight: 8,
      whiteSpace: 'nowrap',
    }}>
      {badge.label}
    </span>
  );
}

// Track D (F5): coverage statement + policy note. Answers "why did half my
// sheet import without details?" before the user has to ask — unmatched
// entries stay name-only BY DESIGN (only SRD-licensed content carries rules
// text), and coverage grows in audited batches.
function SrdCoveragePanel({ coverage }) {
  if (!coverage || coverage.total === 0) return null;
  const collections = Object.entries(coverage.byCollection || {})
    .sort(([a], [b]) => a.localeCompare(b));
  return (
    <section style={panelStyle} aria-label="SRD coverage">
      <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>SRD Coverage</h3>
      <div style={{ fontSize: 15, marginBottom: 6 }}>
        {coverage.srd} of {coverage.total} rules entries matched the local SRD catalog.
      </div>
      {collections.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', fontSize: 13, color: CI.textLabel, marginBottom: 8 }}>
          {collections.map(([collection, tally]) => (
            <span key={collection}>{collection}: {tally.srd}/{tally.srd + tally.placeholders} matched</span>
          ))}
        </div>
      )}
      <div style={{ fontSize: 13, color: CI.textMuted }}>
        Unmatched entries import as name-only placeholders without rules text — by design,
        only SRD-licensed content carries details. SRD coverage grows in audited batches.
      </div>
    </section>
  );
}

// Track D (F6): the full receipt, on demand. The sample lists above stay as
// the glance layer; these <details> hold every entry and diagnostic.
function ReceiptDetails({ summary }) {
  const provenance = Array.isArray(summary.provenance) ? summary.provenance : [];
  const diagnostics = Array.isArray(summary.diagnosticsList) ? summary.diagnosticsList : [];
  if (!provenance.length && !diagnostics.length) return null;
  const grouped = provenance.reduce((next, entry) => {
    (next[entry.collection] ||= []).push(entry);
    return next;
  }, {});
  return (
    <>
      {provenance.length > 0 && (
        <details style={panelStyle}>
          <summary style={{ cursor: 'pointer', fontSize: 15 }}>
            All imported entries ({provenance.length})
          </summary>
          <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([collection, entries]) => (
              <div key={collection}>
                <div style={{ fontSize: 12, color: CI.textMuted, textTransform: 'uppercase', marginBottom: 4 }}>
                  {collection}
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {entries.map((entry, index) => (
                    <li key={`${entry.name}-${index}`} style={{ marginBottom: 2 }}>
                      <ProvenanceBadge sourceKind={entry.sourceKind} />
                      {entry.name}
                      {entry.srdId && (
                        <span style={{ color: CI.textMuted, fontSize: 12 }}> ({entry.srdId})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </details>
      )}
      {diagnostics.length > 0 && (
        <details style={panelStyle}>
          <summary style={{ cursor: 'pointer', fontSize: 15 }}>
            All diagnostics ({diagnostics.length})
          </summary>
          <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
            {diagnostics.map((entry, index) => (
              <li key={`${entry.code}-${index}`} style={{ marginBottom: 2, fontSize: 13 }}>
                <span style={{ color: entry.severity === 'error' ? '#dc5b5b' : entry.severity === 'warning' ? CI.accent : '#8fb5d6' }}>
                  {entry.severity}
                </span>
                {': '}
                {entry.code}
                {entry.path && <span style={{ color: CI.textMuted }}> — {entry.path}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

function ReviewSummary({ summary }) {
  if (!summary) return null;
  return (
    <div style={{ display: 'grid', gap: 14 }} data-testid="import-review-summary">
      <CountGrid summary={summary} />
      <SrdCoveragePanel coverage={summary.coverage} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        <section style={panelStyle}>
          <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Core</h3>
          <div>Name: {summary.characterName || 'Existing character'}</div>
          <div>Mode: {summary.mode}</div>
          <div>Class: {summary.charClass || 'Not present'}</div>
          <div>Level: {summary.level ?? 'Not present'}</div>
          <div>Race: {summary.race || 'Not present'}</div>
          <div>Max HP: {summary.maxHp ?? 'Not present'}</div>
        </section>
        <section style={panelStyle}>
          <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Patch Fields</h3>
          <div style={{ wordBreak: 'break-word' }}>{joinList(summary.patchKeys)}</div>
        </section>
        <section style={panelStyle}>
          <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Content</h3>
          <div>SRD samples: {joinList(summary.samples.srd)}</div>
          <div>Name-only samples: {joinList(summary.samples.placeholders)}</div>
          <div>Spell samples: {joinList(summary.samples.spells)}</div>
        </section>
        <section style={panelStyle}>
          <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Import Status</h3>
          <div>Source: {describeAppPayloadSourceKind(summary.sourceKind)}</div>
          <div>Equipment: {summary.equipmentStatus}</div>
          <div>Diagnostics: {joinList(summary.samples.diagnostics)}</div>
        </section>
      </div>
      <ReceiptDetails summary={summary} />
    </div>
  );
}

export default function CharacterImportReviewModal({
  mode = 'update',
  existingChar = null,
  onClose,
  fetchAppPayload = fetchImportAppPayload,
  initialPayload = null,
  importApiBase = DEFAULT_IMPORT_API_BASE,
  availableSources = [IMPORT_PREVIEW_SOURCES[0]],
  uploadAppPayload = null,
  characterId = existingChar?.id || null,
  onApplyCreate = null,
  onApplyUpdate = null,
  onApplied = null,
  title: titleOverride = null,
  // Optional map of --ci-* CSS custom properties (see the CI token table at
  // the top of this file). Hosts with a theme system (the dashboard) pass
  // these to restyle the modal; omitted, the standalone palette applies.
  themeVars = null,
}) {
  const configuredSourceOptions = useMemo(() => {
    const byId = new Map(IMPORT_PREVIEW_SOURCES.map((source) => [source.id, source]));
    return (Array.isArray(availableSources) && availableSources.length ? availableSources : [IMPORT_PREVIEW_SOURCES[0]])
      .map((source) => (typeof source === 'string' ? source : source?.id))
      .map((sourceId) => byId.get(sourceId))
      .filter(Boolean);
  }, [availableSources]);
  const previewTransport = useMemo(() => (
    fetchAppPayload === fetchImportAppPayload || fetchAppPayload === fetchDdbAppPayload
      ? getImportPreviewTransport({ apiBase: importApiBase })
      : { available: true, message: '' }
  ), [fetchAppPayload, importApiBase]);
  // Fixture sources (dev preview server) are a development transport; the
  // production path is the generic auto-detected PDF upload below, which is
  // not tied to any fixture source.
  const sourceOptions = useMemo(() => (
    previewTransport.available ? configuredSourceOptions : []
  ), [configuredSourceOptions, previewTransport.available]);
  const sourceUnavailable = sourceOptions.length === 0;
  const defaultSourceId = sourceOptions[0]?.id || '';
  const initialReviewMode = mode === 'create' ? 'create' : 'update';
  const reviewModeOptions = useMemo(() => {
    const options = new Set([initialReviewMode]);
    if (typeof onApplyCreate === 'function') options.add('create');
    if (typeof onApplyUpdate === 'function') options.add('update');
    return Array.from(options);
  }, [initialReviewMode, onApplyCreate, onApplyUpdate]);
  const [reviewMode, setReviewMode] = useState(mode === 'create' ? 'create' : 'update');
  const [importSource, setImportSource] = useState(defaultSourceId);
  const [includeEquipment, setIncludeEquipment] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [payload, setPayload] = useState(null);
  const [loadedRequest, setLoadedRequest] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [applyError, setApplyError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  // Create-mode identity confirmation (P10/P14): the sheet's extracted
  // name/race/HP are prefilled but the user confirms or corrects them before
  // the character is created. A sheet with no readable name previously
  // created a character silently named "Imported" — now the name is required.
  const [createOverrides, setCreateOverrides] = useState({ name: '', race: '', maxHp: '' });

  const title = useMemo(() => {
    if (titleOverride) return titleOverride;
    if (reviewMode === 'create') return 'Review Character Import';
    return `Review Character Update${existingChar?.name ? `: ${existingChar.name}` : ''}`;
  }, [existingChar?.name, reviewMode, titleOverride]);
  const selectedSource = sourceOptions.find((source) => source.id === importSource) || sourceOptions[0] || null;
  const previewAvailable = previewTransport.available && !sourceUnavailable;
  const previewUnavailableMessage = sourceUnavailable
    ? configuredSourceOptions.length === 0
      ? 'No supported import preview sources are available in this context. Paste sanitized app-payload JSON below to review it.'
      : previewTransport.message || 'Character import server preview is unavailable here. Paste sanitized app-payload JSON below to review it.'
    : previewTransport.message || 'Character import server preview is unavailable here. Paste sanitized app-payload JSON below to review it.';
  // Hosted upload is auto-detected server-side (D&D Beyond or DiceCloud print
  // PDFs) — it is not tied to a fixture source. The local dev-server upload
  // remains a Shenyi-fixture path.
  const hostedUploadAvailable = typeof uploadAppPayload === 'function';
  const localUploadAvailable = selectedSource?.id === 'shenyi'
    && !!importApiBase
    && previewTransport.available;
  const pdfUploadAvailable = hostedUploadAvailable || localUploadAvailable;
  const nothingAvailable = sourceUnavailable && !pdfUploadAvailable;

  useEffect(() => {
    setReviewMode((currentMode) => (
      reviewModeOptions.includes(currentMode) ? currentMode : initialReviewMode
    ));
  }, [initialReviewMode, reviewModeOptions]);

  useEffect(() => {
    setImportSource(defaultSourceId);
  }, [defaultSourceId]);

  useEffect(() => {
    if (!initialPayload) return;
    try {
      loadPayload(initialPayload, {
        mode: initialPayload?.mode,
        includeEquipment: null,
        source: null,
      });
    } catch (err) {
      setPayload(null);
      setSummary(null);
      setError(err.message || 'Import review payload is invalid.');
    }
    // Initial payloads are intentionally bound to the selected character at first review load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPayload]);

  const loadPayload = (nextPayload, request = null) => {
    // A fixture source pins the expected payload kind; the generic hosted
    // upload is auto-detected server-side, so only the enabled-source-kind
    // allowlist applies (inside validateImportReviewPayload).
    const pinnedSource = request?.source && request.source !== CHARACTER_PDF_UPLOAD_SOURCE
      ? request.source
      : null;
    const safePayload = validateImportReviewPayload(nextPayload, pinnedSource ? { expectedSource: pinnedSource } : {});
    const loadedTargetCharacterId = safePayload.mode === 'update'
      ? (request?.targetCharacterId ?? characterId ?? null)
      : null;
    setPayload(safePayload);
    setLoadedRequest({
      ...(request || { mode: safePayload.mode, includeEquipment: null }),
      mode: safePayload.mode,
      targetCharacterId: loadedTargetCharacterId,
    });
    setSummary(summarizeImportReviewPayload(safePayload));
    setReviewMode(safePayload.mode);
    setCreateOverrides({
      name: typeof safePayload.createArgs?.name === 'string' ? safePayload.createArgs.name : '',
      race: typeof safePayload.createArgs?.race === 'string' ? safePayload.createArgs.race : '',
      maxHp: Number.isFinite(safePayload.createArgs?.maxHp) ? String(safePayload.createArgs.maxHp) : '',
    });
    setError('');
    setApplyError('');
    setConfirmApply(false);
    setApplyResult(null);
  };

  const handleFetchPreview = async () => {
    if (!previewAvailable) {
      setError(previewUnavailableMessage);
      setApplyError('');
      return;
    }
    if (!selectedSource) {
      setError(previewUnavailableMessage);
      setApplyError('');
      return;
    }
    setLoading(true);
    setError('');
    setApplyError('');
    try {
      const request = {
        source: selectedSource.id,
        mode: reviewMode,
        includeEquipment,
      };
      if (importApiBase) request.apiBase = importApiBase;
      const nextPayload = await fetchAppPayload({
        ...request,
      });
      loadPayload(nextPayload, {
        source: selectedSource.id,
        mode: reviewMode,
        includeEquipment,
        targetCharacterId: reviewMode === 'update' ? (characterId || null) : null,
      });
      setPasteText(JSON.stringify(redactPayloadForDisplay(nextPayload), null, 2));
    } catch (err) {
      setError(err.message || 'Import preview failed.');
      setApplyError('');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPreview = async () => {
    if (!pdfUploadAvailable) {
      setError('PDF upload preview is not available for this import selection. Paste sanitized app-payload JSON instead.');
      setApplyError('');
      return;
    }
    if (!uploadFile) {
      setError('Choose a PDF file before uploading.');
      setApplyError('');
      return;
    }
    setUploading(true);
    setError('');
    setApplyError('');
    try {
      const targetCharacterId = reviewMode === 'update' ? (characterId || null) : null;
      let nextPayload = null;
      let loadedSource = null;
      let hostedUploadError = null;
      if (hostedUploadAvailable) {
        try {
          // Hosted uploads are auto-detected server-side — no fixture source.
          nextPayload = await uploadAppPayload({
            source: CHARACTER_PDF_UPLOAD_SOURCE,
            mode: reviewMode,
            includeEquipment,
            file: uploadFile,
          });
        } catch (err) {
          hostedUploadError = err;
        }
      }
      if (!nextPayload && localUploadAvailable && selectedSource) {
        try {
          nextPayload = await fetchImportPdfUploadAppPayload({
            source: selectedSource.id,
            mode: reviewMode,
            includeEquipment,
            file: uploadFile,
            apiBase: importApiBase,
          });
          loadedSource = selectedSource.id;
        } catch (localErr) {
          if (hostedUploadError) throw hostedUploadError;
          throw localErr;
        }
      }
      if (!nextPayload) {
        throw hostedUploadError || new Error('Character import PDF upload failed.');
      }
      loadPayload(nextPayload, {
        source: loadedSource,
        mode: reviewMode,
        includeEquipment,
        targetCharacterId,
      });
      setPasteText(JSON.stringify(redactPayloadForDisplay(nextPayload), null, 2));
    } catch (err) {
      setError(err.message || 'Character import PDF upload failed.');
      setApplyError('');
    } finally {
      setUploading(false);
    }
  };

  const handlePasteReview = () => {
    let parsedPayload;
    try {
      parsedPayload = JSON.parse(pasteText);
    } catch (_err) {
      setError('Import payload JSON is invalid.');
      setApplyError('');
      return;
    }
    try {
      loadPayload(parsedPayload);
    } catch (err) {
      setError(err.message || 'Import payload JSON is invalid.');
      setApplyError('');
    }
  };

  const trimmedCreateName = createOverrides.name.trim();
  const createNameMissing = payload?.mode === 'create' && !trimmedCreateName;

  // Merge the user-confirmed identity fields into the reviewed payload.
  // Only the three CREATE_ARG_KEYS are touched; the rest of the patch and the
  // receipt are applied exactly as reviewed. Race/HP fall back to the
  // payload's values when the field is cleared; the name is required (no
  // silent 'Imported'). A maxHp override must ALSO sync patch.maxHp — the
  // apply path spreads the patch over the createArgs-built shell, so a
  // patch-side sheet value would silently win over the user's correction.
  const buildEffectiveCreatePayload = () => {
    const overrideArgs = {
      ...(payload.createArgs || {}),
      name: trimmedCreateName,
    };
    const trimmedRace = createOverrides.race.trim();
    if (trimmedRace) overrideArgs.race = trimmedRace;
    const parsedMaxHp = Number.parseInt(createOverrides.maxHp, 10);
    let nextPatch = payload.patch;
    if (Number.isFinite(parsedMaxHp)) {
      const boundedMaxHp = Math.max(1, Math.min(999, parsedMaxHp));
      overrideArgs.maxHp = boundedMaxHp;
      if (payload.patch && typeof payload.patch === 'object') {
        nextPatch = { ...payload.patch };
        if (typeof nextPatch.maxHp === 'number') nextPatch.maxHp = boundedMaxHp;
        if (typeof nextPatch.currentHp === 'number' && nextPatch.currentHp > boundedMaxHp) {
          nextPatch.currentHp = boundedMaxHp;
        }
      }
    }
    return { ...payload, createArgs: overrideArgs, patch: nextPatch };
  };

  const handleApply = async () => {
    if (!payload) {
      setError('Load or review an import payload before applying.');
      return;
    }
    if (createNameMissing) {
      setApplyError('Enter a character name before creating.');
      return;
    }
    setApplying(true);
    setApplyError('');
    setApplyResult(null);
    try {
      const effectivePayload = payload.mode === 'create' ? buildEffectiveCreatePayload() : payload;
      const result = await applyReviewedImportPayload(effectivePayload, {
        characterId,
        onApplyCreate,
        onApplyUpdate,
      });
      setApplyResult(result);
      if (typeof onApplied === 'function') onApplied(result);
    } catch (err) {
      setApplyError(safeApplyErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget && typeof onClose === 'function') onClose();
  };

  const applyAvailable = payload?.mode === 'create'
    ? typeof onApplyCreate === 'function'
    : payload?.mode === 'update'
      ? typeof onApplyUpdate === 'function'
      : false;
  const reviewNeedsReload = !!payload && !!loadedRequest && (
    (loadedRequest.mode && reviewMode !== loadedRequest.mode)
    || (loadedRequest.source && importSource !== loadedRequest.source)
    || (loadedRequest.includeEquipment !== null && includeEquipment !== loadedRequest.includeEquipment)
    || (payload?.mode === 'update' && loadedRequest.targetCharacterId !== (characterId || null))
  );
  const applyBlocked = !payload
    || !!error
    || !applyAvailable
    || reviewNeedsReload
    || !!applyResult
    || !confirmApply
    || createNameMissing
    || applying;
  const applyCopy = payload?.mode === 'create' ? 'Create character from reviewed import' : 'Apply reviewed update';

  return (
    <div style={{ ...overlayStyle, ...(themeVars || {}) }} onMouseDown={handleBackdrop} role="presentation">
      <div style={modalStyle} role="dialog" aria-modal="true" aria-labelledby="character-import-review-title">
        <header style={headerStyle}>
          <div>
            <div style={{ fontSize: 12, letterSpacing: 0, color: CI.accent, textTransform: 'uppercase' }}>
              {applyAvailable ? 'Review and apply' : 'Review only'}
            </div>
            <h2 id="character-import-review-title" style={{ margin: '3px 0 4px', fontSize: 22 }}>{title}</h2>
            <div style={{ fontSize: 13, color: CI.textSoft }}>
              {applyAvailable ? 'Changes are applied only after final confirmation.' : 'No character changes are applied from this screen.'}
            </div>
          </div>
          <button type="button" onClick={onClose} style={secondaryButtonStyle} aria-label="Close import review">
            Close
          </button>
        </header>
        <div style={bodyStyle}>
          <section style={panelStyle}>
            <div style={controlsStyle}>
              <label style={labelStyle}>
                Import action
                <select
                  style={inputStyle}
                  value={reviewMode}
                  onChange={(event) => setReviewMode(event.target.value)}
                  aria-label="Import action"
                >
                  {reviewModeOptions.includes('update') && (
                    <option value="update">Update current character</option>
                  )}
                  {reviewModeOptions.includes('create') && (
                    <option value="create">Create new character</option>
                  )}
                </select>
              </label>
              {reviewMode === 'create' && (
                <div style={{ ...panelStyle, padding: 10, alignSelf: 'stretch' }}>
                  Imported characters are linked to your signed-in account.
                </div>
              )}
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'center' }}>
                <input
                  type="checkbox"
                  checked={includeEquipment}
                  onChange={(event) => setIncludeEquipment(event.target.checked)}
                />
                Include equipment payload
              </label>
              {previewAvailable && sourceOptions.length > 1 && (
                <label style={labelStyle}>
                  Dev fixture source
                  <select
                    style={inputStyle}
                    value={importSource}
                    onChange={(event) => setImportSource(event.target.value)}
                    aria-label="Import source"
                  >
                    {sourceOptions.map((source) => (
                      <option key={source.id} value={source.id}>{source.label}</option>
                    ))}
                  </select>
                </label>
              )}
              {previewAvailable && (
                <button
                  type="button"
                  style={loading ? disabledButtonStyle : buttonStyle}
                  onClick={handleFetchPreview}
                  disabled={loading}
                >
                  {loading ? 'Loading review...' : `Load ${selectedSource?.label || 'Import'} Review`}
                </button>
              )}
            </div>
            {nothingAvailable && (
              <div style={{ ...warningPanelStyle, marginTop: 12 }}>
                {previewUnavailableMessage}
              </div>
            )}
          </section>

          {pdfUploadAvailable && (
            <section style={panelStyle}>
              <label style={labelStyle}>
                Character sheet PDF
                <input
                  style={inputStyle}
                  type="file"
                  accept="application/pdf,.pdf"
                  aria-label="Character sheet PDF"
                  onChange={(event) => {
                    setUploadFile(event.target.files?.[0] || null);
                    setError('');
                    setApplyError('');
                  }}
                />
              </label>
              <div style={{ fontSize: 13, color: CI.textSoft, marginTop: 6 }}>
                {hostedUploadAvailable
                  ? 'DDB exports and DiceCloud print PDFs are detected automatically. Scanned or image-based sheets are not supported yet.'
                  : 'Local dev preview upload (Shenyi fixture).'}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
                <button
                  type="button"
                  style={(uploading || !uploadFile) ? disabledButtonStyle : buttonStyle}
                  onClick={handleUploadPreview}
                  disabled={uploading || !uploadFile}
                >
                  {uploading ? 'Uploading PDF...' : 'Upload PDF Review'}
                </button>
                <span style={{ color: CI.textMuted, fontSize: 13 }}>
                  {uploadFile?.name || `Choose a PDF to generate a reviewed ${reviewMode} payload.`}
                </span>
              </div>
            </section>
          )}

          <section style={panelStyle}>
            <label style={labelStyle}>
              App-payload JSON
              <textarea
                style={{ ...inputStyle, minHeight: 130, fontFamily: 'monospace', resize: 'vertical' }}
                value={pasteText}
                onChange={(event) => setPasteText(event.target.value)}
                placeholder="Paste a tracker app-payload JSON object to review it without applying changes."
              />
            </label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
              <button type="button" style={secondaryButtonStyle} onClick={handlePasteReview}>
                Review JSON
              </button>
            </div>
          </section>

          {error && (
            <div role="alert" style={{ ...panelStyle, borderColor: '#d66', background: 'rgba(120, 24, 24, 0.24)' }}>
              {error}
            </div>
          )}
          {applyError && (
            <div role="alert" style={{ ...panelStyle, borderColor: '#d66', background: 'rgba(120, 24, 24, 0.24)' }}>
              Import apply failed: {applyError}
            </div>
          )}

          {payload?.mode === 'create' && !applyResult && (
            <section style={panelStyle} aria-label="New character details">
              <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>New Character Details</h3>
              <div style={{ fontSize: 13, color: CI.textSoft, marginBottom: 10 }}>
                Prefilled from the imported sheet — confirm or correct before creating.
              </div>
              <div style={controlsStyle}>
                <label style={labelStyle}>
                  Character name (required)
                  <input
                    style={inputStyle}
                    type="text"
                    maxLength={80}
                    value={createOverrides.name}
                    aria-label="Character name"
                    placeholder="Enter a character name"
                    onChange={(event) => setCreateOverrides((prev) => ({ ...prev, name: event.target.value }))}
                  />
                </label>
                <label style={labelStyle}>
                  Race / species
                  <input
                    style={inputStyle}
                    type="text"
                    maxLength={80}
                    value={createOverrides.race}
                    aria-label="Character race"
                    onChange={(event) => setCreateOverrides((prev) => ({ ...prev, race: event.target.value }))}
                  />
                </label>
                <label style={labelStyle}>
                  Max HP
                  <input
                    style={inputStyle}
                    type="number"
                    min={1}
                    max={999}
                    value={createOverrides.maxHp}
                    aria-label="Character max HP"
                    onChange={(event) => setCreateOverrides((prev) => ({ ...prev, maxHp: event.target.value }))}
                  />
                </label>
              </div>
              {createNameMissing && (
                <div style={{ ...warningPanelStyle, marginTop: 10 }}>
                  The imported sheet did not include a readable character name. Enter one before creating.
                </div>
              )}
            </section>
          )}

          <section style={panelStyle}>
            <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Apply</h3>
            {reviewNeedsReload && (
              <div style={{ ...warningPanelStyle, marginBottom: 10 }}>
                Import options changed after this review was loaded, or the selected character changed. Load the review again before applying.
              </div>
            )}
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={confirmApply}
                onChange={(event) => setConfirmApply(event.target.checked)}
              />
              I reviewed this import and want to apply it.
            </label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
              <button
                type="button"
                style={applyBlocked ? disabledButtonStyle : dangerButtonStyle}
                disabled={applyBlocked}
                onClick={handleApply}
              >
                {applying ? 'Applying import...' : applyCopy}
              </button>
              {!applyAvailable && (
                <div style={{ color: CI.textMuted, alignSelf: 'center' }}>
                  Apply is unavailable in this context.
                </div>
              )}
            </div>
          </section>

          {payload && !error && (
            <div
              role="status"
              style={{
                ...panelStyle,
                borderColor: reviewNeedsReload ? 'rgba(214, 177, 76, 0.66)' : 'rgba(99, 190, 123, 0.54)',
              }}
            >
              {reviewNeedsReload
                ? 'Review loaded, but options or selected character changed. Reload before applying.'
                : `Review ready — detected ${describeAppPayloadSourceKind(summary?.sourceKind)}. This payload is validated for display.`}
            </div>
          )}
          {applyResult && !error && (
            <div role="status" style={{ ...panelStyle, borderColor: 'rgba(99, 190, 123, 0.54)' }}>
              Import applied to {applyResult.characterId || 'the selected character'}.
            </div>
          )}
          <FlowWarnings payload={payload} summary={summary} />
          <ReviewSummary summary={summary} />
        </div>
      </div>
    </div>
  );
}
